import React, { useRef } from "react";
import { format } from "date-fns";
import { keepPreviousData, useQuery } from "@tanstack/react-query";
import SidePanel, { PanelHeader } from "./side-panel";
import ScaledPreview from "./scaled-preview";
import { useIsMobile } from "../hooks/use-is-mobile";
import { ScrollArea } from "./ui/scroll-area";
import { Icons } from "./ui/icons";
import { getPpePurchases, PpePurchase } from "../lib/api";

interface PpeDetailsOfReceivedDrawerProps {
  anchorPurchase: PpePurchase | null;
  onClose: () => void;
}

const formatQuantity = (value: number) =>
  Number.isInteger(value)
    ? String(value)
    : value.toLocaleString(undefined, { maximumFractionDigits: 4 });

const formatItemName = (purchase: PpePurchase) => {
  if (!purchase.ppe) return "N/A";
  return purchase.ppe.size
    ? `${purchase.ppe.name} (${purchase.ppe.size})`
    : purchase.ppe.name;
};

const PpeDetailsOfReceivedDrawer: React.FC<PpeDetailsOfReceivedDrawerProps> = ({
  anchorPurchase,
  onClose,
}) => {
  const printRef = useRef<HTMLDivElement>(null);
  const isMobile = useIsMobile();

  const batchQuery = useQuery({
    queryKey: ["ppe", "purchase-batch", anchorPurchase?.id],
    queryFn: async () => {
      const response = await getPpePurchases(1, 500, "");
      return response.data.filter(
        (item) =>
          item.purchasedDate === anchorPurchase!.purchasedDate &&
          item.purchasedByUserId === anchorPurchase!.purchasedByUserId &&
          (item.notes ?? null) === (anchorPurchase!.notes ?? null),
      );
    },
    enabled: !!anchorPurchase,
    placeholderData: keepPreviousData,
  });

  const purchases =
    batchQuery.data && batchQuery.data.length > 0
      ? batchQuery.data
      : anchorPurchase
        ? [anchorPurchase]
        : [];

  const purchasedBy = anchorPurchase?.purchasedByUser?.fullName || "N/A";
  const warehouseName = anchorPurchase?.ppe?.warehouse?.name || "N/A";
  const notes = anchorPurchase?.notes?.trim() || "";

  const displayDate = anchorPurchase?.purchasedDate
    ? format(new Date(anchorPurchase.purchasedDate), "MMM dd, yyyy")
    : "N/A";

  const handlePrint = () => {
    if (!printRef.current) return;
    const el = printRef.current;
    const parent = el.parentNode;
    const next = el.nextSibling;
    const originalTitle = document.title;

    document.body.appendChild(el);
    document.title = `Details of Received - ${warehouseName}`;
    window.print();

    document.title = originalTitle;
    if (parent) parent.insertBefore(el, next);
    else el.remove();
  };

  return (
    <SidePanel
      open={!!anchorPurchase}
      onClose={onClose}
      width="w-[900px]"
      layer="overlay"
    >
      {anchorPurchase && (
        <div className="relative h-full flex flex-col">
          <PanelHeader
            title={`Details of Received for ${warehouseName}`}
            onClose={onClose}
            actions={[
              {
                label: "Print",
                icon: Icons.Print,
                onClick: handlePrint,
                disabled: batchQuery.isLoading,
                pinned: true,
              },
            ]}
          />

          <div className="flex-grow overflow-hidden">
            <ScrollArea className="h-full">
              <div
                className={
                  isMobile ? "w-full p-3 bg-gray-50" : "w-full p-6 bg-gray-50"
                }
              >
                <ScaledPreview>
                  <div
                    ref={printRef}
                    className="print-area p-4 bg-white shadow-lg rounded-lg"
                  >
                    <div className="text-center">
                      <h1 className="text-2xl font-bold text-gray-950 mb-2 border-b-2 border-gray-950 inline-block mx-auto">
                        DETAILS OF RECEIVED
                      </h1>
                    </div>
                    <div className="text-center mb-4 border-b-2 border-gray-950 pb-2">
                      <img
                        src="/images/logo.png"
                        alt="Logo"
                        className="h-12 mx-auto mb-2"
                      />
                      <p className="text-sm text-gray-950">
                        Dealers in scaffolding & formwork (sale, rental and
                        fixing, cradle & scissor lift rental and sale)
                      </p>
                      <p className="text-sm text-gray-950">
                        Office: Plot 161, Block 161, Wazo, Tegeta
                      </p>
                      <p className="text-sm text-gray-950">
                        P.O Box 35854, Dar es Salaam, Tanzania. Sales:
                        +255(0)780 973 724 Warehouse: +255(0)780 973 728
                      </p>
                      <p className="text-sm text-gray-950">
                        Email: info@waposcaffolding.com Website:
                        www.waposcaffolding.co.tz
                      </p>
                    </div>

                    <div className="mt-3 border border-gray-950 rounded-xl px-4 py-2 text-xs text-gray-950">
                      <div className="grid grid-cols-2 gap-x-8 gap-y-1">
                        <p>
                          <span className="font-semibold">Received At:</span>{" "}
                          <span className="font-medium">{warehouseName}</span>
                        </p>
                        <p>
                          <span className="font-semibold">Date:</span>{" "}
                          <span className="font-medium">{displayDate}</span>
                        </p>
                        {notes ? (
                          <p className="col-span-2">
                            <span className="font-semibold">Notes:</span>{" "}
                            <span className="font-medium">{notes}</span>
                          </p>
                        ) : null}
                      </div>
                    </div>

                    <div className="mt-4">
                      <div className="text-center text-gray-950 font-semibold mb-2">
                        PPE / NON SCAFFOLDS RECEIVED
                      </div>

                      <table className="w-full border border-gray-950 text-sm">
                        <thead>
                          <tr className="bg-gray-950">
                            <th className="text-white p-2 w-[70px] text-left">
                              No:
                            </th>
                            <th className="text-white p-2 text-left">Item</th>
                            <th className="text-white p-2 w-[90px] text-center">
                              Qty
                            </th>
                            <th className="text-white p-2 w-[90px] text-center">
                              Unit
                            </th>
                            <th className="text-white p-2 text-left">
                              Warehouse
                            </th>
                          </tr>
                        </thead>
                        <tbody>
                          {purchases.length > 0 ? (
                            purchases.map((item, idx) => (
                              <tr key={item.id}>
                                <td className="border border-gray-950 px-2 py-1 text-gray-950">
                                  {String(idx + 1).padStart(2, "0")}.
                                </td>
                                <td className="border border-gray-950 px-2 py-1 text-gray-950">
                                  {formatItemName(item)}
                                </td>
                                <td className="border border-gray-950 px-2 py-1 text-center text-gray-950">
                                  {formatQuantity(Number(item.quantity))}
                                </td>
                                <td className="border border-gray-950 px-2 py-1 text-center text-gray-950">
                                  {item.ppe?.unit || "—"}
                                </td>
                                <td className="border border-gray-950 px-2 py-1 text-gray-950">
                                  {item.ppe?.warehouse?.name || "—"}
                                </td>
                              </tr>
                            ))
                          ) : (
                            <tr>
                              <td
                                colSpan={5}
                                className="border border-gray-950 px-2 py-6 text-center text-gray-950"
                              >
                                No items found
                              </td>
                            </tr>
                          )}
                        </tbody>
                      </table>
                    </div>

                    <div className="mt-10 grid grid-cols-2 gap-6 text-xs text-gray-950">
                      <div className="space-y-3">
                        <p className="font-semibold">RECEIVED BY:</p>
                        <p>Name: _____________________________</p>
                        <p>Signature: ________________________</p>
                        <p>Date: _____________________________</p>
                      </div>
                      <div className="space-y-3">
                        <p className="font-semibold">RECORDED BY:</p>
                        <p>Name: {purchasedBy}</p>
                        <p>Signature: ________________________</p>
                        <p>
                          Date:{" "}
                          {anchorPurchase.createdAt
                            ? format(
                                new Date(anchorPurchase.createdAt),
                                "MMM dd, yyyy",
                              )
                            : "_____________________________"}
                        </p>
                      </div>
                    </div>

                    <div className="flex justify-between mt-8 text-xs text-gray-950">
                      <p>
                        Prepared by:{" "}
                        <span className="font-semibold">{purchasedBy}</span>
                      </p>
                      <p>
                        Print Date:{" "}
                        <span className="font-semibold">
                          {format(new Date(), "MMM dd, yyyy")}
                        </span>
                      </p>
                    </div>
                  </div>
                </ScaledPreview>
              </div>
            </ScrollArea>
          </div>
        </div>
      )}
    </SidePanel>
  );
};

export default PpeDetailsOfReceivedDrawer;
